import {
  Control,
  Icon,
  IconText,
  A,
  Input,
  Navbar,
  NavbarEnd,
  NavbarItem,
  Subtitle,
  Title,
  Div,
} from "trunx";
import {
  IconDefinition,
  faBars,
  faBookmark,
  faCloudRain,
  faCloudSunRain,
  faLayerGroup,
  faSearch,
  faTape,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IPanel } from "./models/Menus";
import { LangSelect } from "./shared/LangSelect";
import config from "./config";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useLocaleContext } from "./contexts/LocaleContext";
import { useRootStore } from "./models/Root";

const panelIcons: Record<IPanel, IconDefinition> = {
  sensors: faCircle,
  layers: faLayerGroup,
  radar: faCloudRain,
  forecast: faCloudSunRain,
  bookmarks: faBookmark,
  measure: faTape,
  find: faSearch,
};

const Navbar_ = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const {
    dict: { nav: dict, panel: panelDict },
  } = useLocaleContext();

  const {
    menus: { toggleNav, closeNav, setFind, find, panel, togglePanel },
    layers,
    isMobile,
  } = useRootStore();

  useEffect(() => {
    if (find === "" && panel !== "find") {
      setSearchOpen(false);
    }
  }, [find, panel]);

  const panelIsAvailable = (panel: IPanel) => {
    if (panel === "layers") return layers.any;
    if (panel === "radar") return config.radar;
    if (panel === "forecast") return config.forecast === true;
    return true;
  };

  const PanelLink = ({
    panel,
    icon,
  }: {
    panel: IPanel;
    icon: IconDefinition;
  }) => (
    <A bulma="navbar-item" onClick={() => togglePanel(panel)}>
      <IconText>
        <Icon>
          <FontAwesomeIcon icon={icon} />
        </Icon>
        <span>{panelDict[panel].title}</span>
      </IconText>
    </A>
  );

  if (isMobile) {
    const quickAccess = config.mobile.quickAccess.filter(panelIsAvailable);

    return (
      <Navbar className="mobile-toolbar" style={{ zIndex: 1000 }}>
        <A
          bulma="navbar-item"
          className="mobile-toolbar-action"
          aria-label="Menu"
          title="Menu"
          onClick={toggleNav}
        >
          <Icon>
            <FontAwesomeIcon icon={faBars} />
          </Icon>
        </A>
        <NavbarItem className="mobile-title">
          <Subtitle>{config.title}</Subtitle>
        </NavbarItem>
        {quickAccess.map((panel) => (
          <A
            bulma="navbar-item"
            className="mobile-toolbar-action"
            aria-label={panelDict[panel].title}
            title={panelDict[panel].title}
            data-panel={panel}
            key={panel}
            onClick={() => togglePanel(panel)}
          >
            <Icon>
              <FontAwesomeIcon icon={panelIcons[panel]} />
            </Icon>
          </A>
        ))}
        <A
          bulma="navbar-item"
          className="mobile-toolbar-action"
          aria-label={dict.search}
          title={dict.search}
          aria-expanded={searchOpen}
          onClick={() => {
            closeNav();
            if (searchOpen) setFind("");
            setSearchOpen(!searchOpen);
          }}
        >
          <Icon>
            <FontAwesomeIcon icon={faSearch} />
          </Icon>
        </A>
        {searchOpen && (
          <NavbarItem className="mobile-search">
            <Control hasIconsRight>
              <Input
                id="find"
                bulma="has-background-dark"
                placeholder={dict.search}
                aria-label={dict.search}
                onChange={(e) => setFind(e.target.value)}
                value={find}
                type="search"
                autoComplete="off"
                autoFocus
              ></Input>
              <Icon className={"is-right"}>
                <FontAwesomeIcon icon={faSearch} />
              </Icon>
            </Control>
          </NavbarItem>
        )}
      </Navbar>
    );
  }

  return (
    <Navbar style={{ zIndex: 1000 }}>
      <A bulma="navbar-item" onClick={toggleNav}>
        <Icon>
          <FontAwesomeIcon icon={faBars} />
        </Icon>
      </A>
      <NavbarItem bulma="has-dropdown" className="navbar-title-search">
        <Div bulma="level">
          <Div bulma="level-left">
            <Div bulma="level-item">
              <Title>{config.title}</Title>
            </Div>
            <Div bulma="level-item">
              <Control hasIconsRight>
                <Input
                  bulma="has-background-dark"
                  placeholder={dict.search}
                  onChange={(e) => setFind(e.target.value)}
                  value={find}
                ></Input>
                <Icon className={"is-right"}>
                  <FontAwesomeIcon icon={faSearch} />
                </Icon>
              </Control>
            </Div>
          </Div>
        </Div>
      </NavbarItem>
      <PanelLink panel="sensors" icon={faCircle} />
      {layers.any && <PanelLink panel="layers" icon={faLayerGroup} />}
      {config.radar && <PanelLink panel="radar" icon={faCloudRain} />}
      {config.forecast && (
        <PanelLink panel="forecast" icon={faCloudSunRain} />
      )}
      <PanelLink panel="bookmarks" icon={faBookmark} />
      <PanelLink panel="measure" icon={faTape} />
      <NavbarEnd>
        <A bulma="navbar-item">
          <LangSelect />
        </A>
      </NavbarEnd>
    </Navbar>
  );
};

export default observer(Navbar_);
